import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import generatedTenant from "./tenant.generated.json";
import flowTenant from "../../../../tenants/eva-flow.json";
import type { PlatformModule, TenantConfig } from "@yatta/tenant-config";
import { parseTenantConfig, resolveModules } from "@yatta/tenant-config";

interface TenantRuntimeContextValue {
  tenant: TenantConfig;
  modules: PlatformModule[];
  loading: boolean;
  error?: string;
  hasModule: (module: PlatformModule) => boolean;
}

const TenantRuntimeContext = createContext<TenantRuntimeContextValue | null>(null);

type TenantProfile = "cruzeiro" | "flow";

function selectTenantProfile(buildProfile: string | undefined, hostname: string): TenantProfile {
  const requestedProfile = buildProfile?.trim().toLowerCase();
  if (requestedProfile === "flow") return "flow";
  if (requestedProfile === "cruzeiro") return "cruzeiro";
  if (hostname.trim().toLowerCase() === "evaflow.yattatecnologia.com") return "flow";
  return "cruzeiro";
}

function getInitialTenantConfig(): TenantConfig {
  const buildProfile = import.meta.env.VITE_TENANT_PROFILE as string | undefined;
  const hostname = typeof window === "undefined" ? "" : window.location.hostname;
  const profile = selectTenantProfile(buildProfile, hostname);
  return parseTenantConfig(profile === "flow" ? flowTenant : generatedTenant);
}

function applyBranding(tenant: TenantConfig) {
  const root = document.documentElement;
  root.classList.add("light");
  root.style.setProperty("--tenant-primary", tenant.branding.primaryColor);
  root.style.setProperty("--tenant-secondary", tenant.branding.secondaryColor);
  root.style.setProperty("--tenant-accent", tenant.branding.accentColor);
  root.style.setProperty("--tenant-background", tenant.branding.backgroundColor);
  document.title = tenant.branding.productName;
}

export function TenantRuntimeProvider({ children }: { children: ReactNode }) {
  const fallback = useMemo(getInitialTenantConfig, []);
  const [tenant, setTenant] = useState<TenantConfig>(fallback);
  const [loading, setLoading] = useState(Boolean(import.meta.env.VITE_TENANT_RUNTIME_URL));
  const [error, setError] = useState<string>();

  useEffect(() => {
    applyBranding(tenant);
  }, [tenant]);

  useEffect(() => {
    const runtimeUrl = import.meta.env.VITE_TENANT_RUNTIME_URL as string | undefined;
    if (!runtimeUrl) return;

    const controller = new AbortController();
    const slug = (import.meta.env.VITE_TENANT_SLUG as string | undefined) ?? fallback.slug;
    const url = new URL(runtimeUrl);
    url.searchParams.set("slug", slug);
    url.searchParams.set("hostname", window.location.hostname);

    void fetch(url, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Tenant runtime HTTP ${response.status}`);
        return response.json() as Promise<unknown>;
      })
      .then((body) => {
        setTenant(parseTenantConfig(body));
        setError(undefined);
      })
      .catch((runtimeError: unknown) => {
        if (runtimeError instanceof DOMException && runtimeError.name === "AbortError") return;
        setError(runtimeError instanceof Error ? runtimeError.message : "Falha no tenant runtime");
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [fallback.slug]);

  const modules = useMemo(() => resolveModules(tenant.modules), [tenant.modules]);
  const moduleSet = useMemo(() => new Set(modules), [modules]);

  const value = useMemo<TenantRuntimeContextValue>(
    () => ({
      tenant,
      modules,
      loading,
      ...(error ? { error } : {}),
      hasModule: (module) => moduleSet.has(module),
    }),
    [tenant, modules, loading, error, moduleSet],
  );

  return <TenantRuntimeContext.Provider value={value}>{children}</TenantRuntimeContext.Provider>;
}

export function useTenantRuntime(): TenantRuntimeContextValue {
  const context = useContext(TenantRuntimeContext);
  if (!context) throw new Error("useTenantRuntime must be used within TenantRuntimeProvider.");
  return context;
}
